import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { EnvelopeIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useOrganizationDomains } from '../../../hooks/useOrganizationDomains';
import { useImapServers } from '../../../hooks/useImapServers';
import type { MailDomain, MailDomainCreateRequest } from '../../../types/models/MailDomain';
import type { ApiResponseMailDomainSingle } from '../../../types/api/ApiResponseMailDomain';

interface MailDomainCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The list page owns the mail-domain list; passing it in lets the modal compute which organization
   * domains are still free without a second `useMailDomains` instance. After each create the parent
   * refetches (see `onCreated`), this prop updates, and the just-created domain drops out of the choices.
   */
  mailDomains: MailDomain[];
  /**
   * Refresh the parent's mail-domain list. Returns a promise so "Create and add another" can await the
   * refetch before re-enabling the form, guaranteeing the just-created domain is gone from the choices.
   */
  onCreated: () => Promise<void>;
}

type SubmitMode = 'navigate' | 'addAnother';

function MailDomainCreateModal({ isOpen, onClose, mailDomains, onCreated }: MailDomainCreateModalProps) {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  // The org-domain and IMAP-server lists don't change on create, so fetch them once when the modal
  // opens (gated on `isOpen`, mirroring usePrices) rather than on every list-page render.
  const { organizationDomains, loading: orgLoading, error: orgError } = useOrganizationDomains(isOpen);
  const { imapServers, loading: imapLoading, error: imapError } = useImapServers(isOpen);

  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [selectedImapKey, setSelectedImapKey] = useState('');
  const [submittingMode, setSubmittingMode] = useState<SubmitMode | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const isSubmitting = submittingMode !== null;
  const loading = orgLoading || imapLoading;
  const fetchError = orgError || imapError;

  const availableDomains = useMemo(() => {
    const usedIds = new Set(mailDomains.map((md) => md.organization_domain_id));
    return organizationDomains.filter((d) => d.status === 'verified' && !usedIds.has(d.id));
  }, [organizationDomains, mailDomains]);

  const imapKey = (server: { id: number | null; type: string | null; is_default: boolean }) =>
    `${server.id ?? 'default'}-${server.type ?? 'shared'}`;

  const resetForm = () => {
    setSelectedDomainId('');
    setSelectedImapKey('');
    setCreateError(null);
  };

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (mode: SubmitMode) => {
    setSubmittingMode(mode);
    setCreateError(null);

    let netbox_imap_server_id: number | null = null;
    let netbox_imap_server_type: MailDomainCreateRequest['netbox_imap_server_type'] = null;

    if (imapServers.length === 1 && imapServers[0].is_default) {
      // shared default — send nulls
    } else if (imapServers.length === 1 && !imapServers[0].is_default) {
      netbox_imap_server_id = imapServers[0].id;
      netbox_imap_server_type = imapServers[0].type;
    } else if (selectedImapKey) {
      const selected = imapServers.find((s) => imapKey(s) === selectedImapKey);
      if (selected && !selected.is_default) {
        netbox_imap_server_id = selected.id;
        netbox_imap_server_type = selected.type;
      }
    }

    const body: MailDomainCreateRequest = {
      organization_domain_id: Number(selectedDomainId),
      netbox_imap_server_id,
      netbox_imap_server_type,
    };

    try {
      const response = await apiFetch('/emails/domains', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller exceptions
      // (including validation errors) to `status: 'error'` at HTTP 200, so `response.ok` can be true on
      // a failure.
      const result = (await response.json().catch(() => null)) as ApiResponseMailDomainSingle | null;

      if (result?.status === 'success' && result.data?.mail_domain) {
        const created = result.data.mail_domain;
        addNotification({
          title: 'Mail domain created',
          srTitle: 'Success alert',
          description: `Mail domain ${created.organization_domain_name} was created.`,
          variant: 'success',
        });
        setDrawerOpen(true);

        if (mode === 'navigate') {
          navigate(`/datacenter/emails/${created.id}`);
          return;
        }

        // "Create and add another": refresh the parent list first so the just-created domain drops out
        // of the choices, then reset the form and keep the modal open for the next one.
        await onCreated();
        resetForm();
        return;
      }

      throw new Error(result?.message ?? 'Failed to create mail domain');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setCreateError(message);
      addNotification({
        title: 'Mail domain creation failed',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setSubmittingMode(null);
    }
  };

  const renderImapField = () => {
    if (imapServers.length === 1 && imapServers[0].is_default) {
      return (
        <FormGroup label="IMAP server" fieldId="imap-server">
          <Content>Mail will be hosted on {imapServers[0].name} (shared).</Content>
        </FormGroup>
      );
    }

    if (imapServers.length === 1 && !imapServers[0].is_default) {
      return (
        <FormGroup label="IMAP server" fieldId="imap-server">
          <Content>Mail will be hosted on {imapServers[0].name} (dedicated).</Content>
        </FormGroup>
      );
    }

    return (
      <FormGroup label="IMAP server" isRequired fieldId="imap-server">
        <FormSelect
          id="imap-server"
          value={selectedImapKey}
          onChange={(_event, value) => setSelectedImapKey(value)}
          aria-label="Select IMAP server"
          isRequired
        >
          <FormSelectOption value="" label="Select an IMAP server..." isDisabled />
          {imapServers.map((server) => (
            <FormSelectOption
              key={imapKey(server)}
              value={imapKey(server)}
              label={server.is_default ? `${server.name} (shared)` : `${server.name} (dedicated)`}
            />
          ))}
        </FormSelect>
      </FormGroup>
    );
  };

  const renderBody = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (fetchError) {
      return (
        <Alert variant="danger" isInline title="Failed to load form data">
          {fetchError}
        </Alert>
      );
    }

    if (availableDomains.length === 0) {
      return (
        <EmptyState titleText="No available domains" icon={EnvelopeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            No verified organization domains are available. Add and verify a domain first.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/organization/domains" />}>
                Manage domains
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Form>
        {createError && (
          <Alert variant="danger" isInline title="Creation failed" className="pf-v6-u-mb-md">
            {createError}
          </Alert>
        )}

        <FormGroup label="Organization domain" isRequired fieldId="org-domain">
          <FormSelect
            id="org-domain"
            value={selectedDomainId}
            onChange={(_event, value) => setSelectedDomainId(value)}
            aria-label="Select an organization domain"
            isRequired
          >
            <FormSelectOption value="" label="Select a domain..." isDisabled />
            {availableDomains.map((d) => (
              <FormSelectOption key={d.id} value={String(d.id)} label={d.name} />
            ))}
          </FormSelect>
        </FormGroup>

        {renderImapField()}
      </Form>
    );
  };

  const canCreate = !loading && !fetchError && availableDomains.length > 0 && Boolean(selectedDomainId);

  return (
    <Modal variant="small" isOpen={isOpen} onClose={resetAndClose} aria-label="Create mail domain">
      <ModalHeader title="Create mail domain" />
      <ModalBody>{renderBody()}</ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => handleSubmit('navigate')}
          isDisabled={!canCreate || isSubmitting}
          isLoading={submittingMode === 'navigate'}
        >
          Create
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleSubmit('addAnother')}
          isDisabled={!canCreate || isSubmitting}
          isLoading={submittingMode === 'addAnother'}
        >
          Create and add another
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailDomainCreateModal;
